import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { AdminHeader } from '@/features/admin/ui';
import { adminQueries } from '@/lib/queries/admin';

export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
});

function AdminOverview() {
  const stats = useQuery(adminQueries.dashboardStats());
  const audit = useQuery(adminQueries.audit(0));
  const s = stats.data;

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Overview" title="Editorial operations." />

      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          to="/admin/applications"
          label="Pending applications"
          value={s?.pendingApplications}
        />
        <Stat to="/admin/content" label="Pending content" value={s?.pendingContent} />
        <Stat to="/admin/invites" label="Waitlist" value={s?.waitlistPending} />
        <Stat to="/admin/users" label="Active subscribers" value={s?.activeSubscribers} />
      </div>

      <section className="mt-12">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-slate">Quick actions</p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Action to="/admin/invites" label="Generate invite codes" />
          <Action to="/admin/content" label="Review next pending content" />
          <Action to="/admin/applications" label="Review next application" />
          <Action to="/admin/masthead" label="Add masthead entry" />
        </div>
      </section>

      <section className="mt-12">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-slate">Recent activity</p>
        <ul className="mt-3 divide-y divide-ink/10">
          {(audit.data ?? []).slice(0, 10).map((a) => (
            <li key={a.id} className="py-2 font-sans text-sm text-ink/70">
              <span className="text-ink/40">{new Date(a.created_at).toLocaleString()} — </span>
              <span className="text-ink">{a.action}</span>
              {a.actor?.display_name && (
                <span className="text-ink/50"> by {a.actor.display_name}</span>
              )}
            </li>
          ))}
          {(audit.data ?? []).length === 0 && (
            <li className="py-2 font-sans text-sm text-ink/50">No recorded activity yet.</li>
          )}
        </ul>
      </section>
    </div>
  );
}

function Stat({ to, label, value }: { to: string; label: string; value: number | undefined }) {
  return (
    <Link
      to={to}
      className="rounded-md border border-slate/30 bg-bg-light/40 p-4 hover:border-oxblood/50"
    >
      <p className="font-serif text-3xl text-ink">{value ?? '—'}</p>
      <p className="mt-1 font-sans text-xs text-ink/60">{label}</p>
    </Link>
  );
}

function Action({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="rounded-md border border-ink/20 px-4 py-2 font-sans text-sm text-ink hover:border-oxblood hover:text-oxblood"
    >
      {label}
    </Link>
  );
}
